import i18n from "../../../locales/i18n";
import {
  firstImageOfListingTemplateInput,
  secondImageOfListingTemplateInput,
  includeBsadInput,
  mainListingInput,
  mainTemplateColorInput,
  realtorImageTemplateInput,
  includeRealtorImageInput,
  realtorLogoTemplateInput,
  realtorNameTemplateInput,
  realtorPhoneNumberTemplateInput,
  getBsadPrompt,
  getRealtorImagePrompt,
  getVerticalFooterPrompt,
  getVisualStylePrompt,
  getColorInfo,
  getInputImageLabel,
  hasRealtorImage,
  promptGenerationFinalParams,
  templateDataType,
  promptGeneratorInitialParams
} from "../template_sections";

export const area_highlight_var_1: templateDataType = {
  templateName: i18n.t("creativesGenerator.templates.areaHighlight.templateName"),
  displayImageUrl: "/static/creative-template-display-images/real-estate-area-highlight-1.jpg",
  description: i18n.t("creativesGenerator.templates.areaHighlight.description"),
  inputs: [
    includeBsadInput,
    mainTemplateColorInput,
    mainListingInput,
    realtorNameTemplateInput,
    realtorPhoneNumberTemplateInput,
    realtorImageTemplateInput,
    includeRealtorImageInput,
    realtorLogoTemplateInput,
    firstImageOfListingTemplateInput,
    secondImageOfListingTemplateInput,
    {
      label: i18n.t("creativesGenerator.templates.areaHighlight.mainTitle"),
      type: "text",
      nameInPrompt: "main_title",
      showInSection: "template_specific",
      description: i18n.t("creativesGenerator.templates.areaHighlight.mainTitleDescription"),
      defaultValue: (params: promptGeneratorInitialParams) => {
        const mainListing = params.selectedListings["main_listing"];
        return i18n.t("creativesGenerator.templates.areaHighlight.mainTitleDefaultValue", {
          city: mainListing?.address?.city ?? "_____"
        });
      }
    },
    {
      label: i18n.t("creativesGenerator.templates.areaHighlight.areaName"),
      type: "text",
      nameInPrompt: "area_name",
      showInSection: "template_specific",
      description: i18n.t("creativesGenerator.templates.areaHighlight.areaNameDescription"),
      defaultValue: (params: promptGeneratorInitialParams) => {
        const mainListing = params.selectedListings["main_listing"];
        const neighborhood = mainListing?.address?.neighborhood;
        const city = mainListing?.address?.city;
        if (neighborhood && city) return `${neighborhood}, ${city}`;
        if (city) return city;
        return "_____";
      }
    },
    {
      label: i18n.t("creativesGenerator.templates.areaHighlight.subtitle"),
      type: "text",
      nameInPrompt: "subtitle_text",
      showInSection: "template_specific",
      description: i18n.t("creativesGenerator.templates.areaHighlight.subtitleDescription"),
      defaultValue: (params: promptGeneratorInitialParams) => {
        const mainListing = params.selectedListings["main_listing"];
        return i18n.t("creativesGenerator.templates.areaHighlight.subtitleDefaultValue", {
          roomsCount: mainListing?.roomsCount ?? "____",
          neighborhood: mainListing?.address?.neighborhood ?? "____"
        });
      }
    }
  ],
  promptGenerator: (params: promptGenerationFinalParams) => {
    const { mainColorValue, lighterColorValue, darkerColorValue } = getColorInfo(params);
    const textParams = params.templateFinalInputs.filter((input) => input.type === "text");

    const mainTitle = textParams.find((param) => param.nameInPrompt === "main_title")?.finalValue;
    const areaName = textParams.find((param) => param.nameInPrompt === "area_name")?.finalValue;
    const subtitleText = textParams.find(
      (param) => param.nameInPrompt === "subtitle_text"
    )?.finalValue;

    return `
{
  "description": "Real Estate Area Highlight Flyer",
  "project_context": {
    "type": "Real Estate Social Media Graphic",
    "tone": "Professional, modern, bold, aspirational",
    "aspect_ratio": "1:1 (Square)"
  },
  ${getVisualStylePrompt(params)},
  ${getBsadPrompt(params)}

  "layout": {
    "type": "diagonal_split_with_hero_title",
    "sections": [
      {
        "name": "background_image_layer",
        "description": "The first listing image fills the entire canvas as a full-bleed background. The property photo must remain clearly visible everywhere — it is the main visual of the design.",
        "source": "listing_image",
        "style": {
          "object_fit": "cover",
          "overlay": {
            "type": "multi_stop_vertical_gradient",
            "description": "A smooth vertical gradient overlay with two purposes: (1) a soft semi-transparent white glow in the top 30% (rgba(255,255,255,0.35) fading smoothly to fully transparent by 40%) to give the title text contrast, and (2) a gradual dark fade starting from 70% height down to the bottom (transparent to rgba(0,0,0,0.5)) for the footer area. The middle portion of the image (40%-70%) must have NO overlay at all — the listing photo should be fully visible there."
          }
        }
      },
      {
        "name": "hero_title_section",
        "position": "top-center, occupying roughly top 30% of the canvas, directly over the listing photo",
        "content": [
          {
            "type": "main_headline",
            "text": "${mainTitle}",
            "style": {
              "font_color": "white",
              "font_size": "very_large",
              "font_weight": "extra_bold",
              "text_shadow": "strong dark shadow for legibility (0 2px 12px rgba(0,0,0,0.7))",
              "text_align": "center",
              "max_width": "85%",
              "line_height": "tight"
            },
            "description": "The title must be rendered directly on top of the listing photo — NOT on a solid color block. The soft white glow in the background gradient provides enough contrast. The text shadow adds additional readability."
          }
        ]
      },
      {
        "name": "area_badge",
        "position": "centered, just below the hero title",
        "content": [
          {
            "type": "location_badge",
            "text": "${areaName}",
            "style": {
              "background_color": "${mainColorValue}",
              "font_color": "white",
              "font_size": "medium",
              "font_weight": "bold",
              "padding": "8px 24px",
              "border_radius": "full_rounded_pill",
              "text_transform": "uppercase"
            },
            "description": "A pill-shaped badge with the area/neighborhood name. Clean and modern, floating over the background. Should have a subtle shadow for depth."
          }
        ]
      },
      {
        "name": "secondary_image_section",
        "position": "bottom-right area, roughly 35% width and 40% height of canvas",
        "content": [
          {
            "type": "image",
            "source": "listing_image_2",
            "style": {
              "object_fit": "cover",
              "border_radius": "12px",
              "border": "3px solid white",
              "box_shadow": "strong shadow for depth and floating effect"
            },
            "description": "The second listing image displayed as a floating card/photo overlaid on the bottom-right of the background. It should look like a polaroid or card placed on top of the scene, rotated very slightly (1-3 degrees) for a natural feel."
          }
        ]
      },
      {
        "name": "subtitle_bar",
        "position": "bottom area, just above the footer, spanning full width",
        "content": [
          {
            "type": "text_bar",
            "text": "${subtitleText}",
            "style": {
              "background_color": "${darkerColorValue}",
              "background_opacity": 0.85,
              "font_color": "white",
              "font_size": "medium",
              "font_weight": "semi_bold",
              "text_align": "center",
              "padding": "10px 20px"
            },
            "description": "A semi-transparent bar with the property subtitle/description. Spans the full width, positioned just above the footer."
          }
        ]
      }
    ]
  },
  ${hasRealtorImage(params) ? `${getRealtorImagePrompt(
    params,
    "The realtor should overlap the footer and the subtitle bar slightly to create depth."
  )},` : ""}
  ${getVerticalFooterPrompt(params)},
  "input_images": {
    "listing_image": "${getInputImageLabel("listing_image")}",
    "listing_image_2": "${getInputImageLabel("listing_image_2")}",
    ${hasRealtorImage(params) ? `"realtor_image": "${getInputImageLabel("realtor_image")}",` : ""}
    "realtor_logo": "${getInputImageLabel("realtor_logo")}"
  },
  "important_notes": [
    "The hero title must be VERY LARGE and BOLD - it is the focal point of the entire design. It MUST be placed over the listing photo, NOT on a solid color background",
    "The listing photo must be clearly visible through the middle of the canvas with no color overlay — only a soft white glow at the top for title readability and a dark fade at the very bottom for the footer",
    "The area badge should be clean, modern, and clearly readable",
    "The second image should feel like a floating card overlaid on the main background - NOT a flat split layout",
    "Ensure the final image is vibrant and eye-catching to stand out on social media feeds",
    "Maintain a professional yet modern tone suitable for real estate marketing",
    "DO NOT alter the provided images beyond removing backgrounds and resizing/cropping as necessary for composition",
    "The overall feel should be fresh and contemporary, like a high-end real estate magazine ad"
  ]
}`;
  }
};
